import { useEffect, useRef } from 'react';
import {
  BarController, BarElement, CategoryScale, Chart as ChartJS, LineController, LineElement, LinearScale,
  PointElement, Tooltip, type ChartConfiguration,
} from 'chart.js';
import { useTheme } from '@/hooks/useTheme';
import { tokenColor } from '@/components/Chart';
import { formatNumber } from '@/lib/format';

ChartJS.register(BarController, BarElement, LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip);

export interface CountSeries {
  key: string;
  label: string;
  /** Token CSS da cor — a cor segue a série. */
  token: string;
  mark: 'bar' | 'line';
  values: number[];
  /** Linha no eixo da direita (escala própria, ex.: mensagens). */
  secondary?: boolean;
}

interface CountChartProps {
  labels: string[];
  titles: string[];
  series: CountSeries[];
  ariaLabel: string;
  height?: number;
}

/**
 * Gráfico de contagens (leads, mensagens): colunas lado a lado e uma linha por
 * cima, com legenda sempre à vista. Parente do SeriesChart, que é de dinheiro.
 */
export function CountChart({ labels, titles, series, ariaLabel, height = 240 }: CountChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { resolved: theme } = useTheme();
  const hasSecondary = series.some((item) => item.secondary);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const muted = tokenColor('--muted-foreground');
    const grid = tokenColor('--border');

    const datasets: ChartConfiguration['data']['datasets'] = series.map((item) => {
      const color = tokenColor(item.token);
      return item.mark === 'bar'
        ? { type: 'bar' as const, label: item.label, data: item.values, backgroundColor: color, borderRadius: 3, maxBarThickness: 18, order: 2, yAxisID: 'y' }
        : {
            type: 'line' as const,
            label: item.label,
            data: item.values,
            borderColor: color,
            backgroundColor: color,
            borderWidth: 2,
            tension: 0.3,
            pointRadius: item.values.length > 40 ? 0 : 2.5,
            order: 1,
            yAxisID: item.secondary ? 'y2' : 'y',
          };
    });

    const ticks = { color: muted, font: { size: 11 }, precision: 0, callback: (value: string | number) => formatNumber(Number(value)) };
    const chart = new ChartJS(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: tokenColor('--popover'),
            borderColor: grid,
            borderWidth: 1,
            titleColor: tokenColor('--popover-foreground'),
            bodyColor: tokenColor('--popover-foreground'),
            padding: 10,
            callbacks: {
              title: (items) => titles[items[0]?.dataIndex ?? 0] ?? '',
              label: (context) => ` ${context.dataset.label}: ${formatNumber(Number(context.parsed.y))}`,
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: muted, font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
          y: { beginAtZero: true, grid: { color: grid }, border: { display: false }, ticks },
          ...(hasSecondary ? { y2: { beginAtZero: true, position: 'right' as const, grid: { display: false }, border: { display: false }, ticks } } : {}),
        },
      },
    });
    return () => chart.destroy();
  }, [labels, titles, series, theme, hasSecondary]);

  return (
    <div className="series-chart">
      <div className="chart-legend" aria-hidden>
        {series.map((item) => (
          <span key={item.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span className={`series-key${item.mark === 'line' ? ' is-line' : ''}`} style={{ background: `var(${item.token})` }} />
            {item.label}
            {item.secondary ? <span className="muted"> (eixo da direita)</span> : null}
          </span>
        ))}
      </div>
      <div style={{ position: 'relative', height }}>
        <canvas ref={canvasRef} role="img" aria-label={ariaLabel} />
      </div>
    </div>
  );
}
